import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Paper,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import {
  CloseButtonWrapper,
  CloseButton,
  ModalWrapper,
} from "../style/modalStyle";
import Modal from "@mui/material/Modal";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceList, setupInvoiceAccount } from "../../stores/clients";
import { setToast } from "../../stores/toast/toastslice";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { validationSchema } from "../../form-schema";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import { CrossIcon, ModalTop, ModalBottom } from "../../assets/Icon/ModalIcon";
import { encryptKeys } from "../../utils/functions";

const STRIPE_DECRYPT_KEY = process.env.REACT_APP_STRIPE_DECRYPT_KEY || "";

const InvoiceForm = ({ setPage }: { setPage: () => void }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const navigate = useNavigate();

  // State for password visibility
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showPassword3, setShowPassword3] = useState(false);
  const [apiError, setApiError] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useDispatch();

  const { data, clientListStatus, total_records, error } = useSelector(
    (state: any) => state.clients
  );

  useEffect(() => {
    dispatch<any>(getInvoiceList({ offset: 0, search: "", filter_by: -1 }));
  }, [dispatch]);

  const accountDetails = data?.account || {};

  const initialValues = {
    secretKey: accountDetails.secret_key || "",
    accountId: accountDetails.account_id || "",
  };

  const handleSubmit = async (
    values: typeof initialValues,
    { setSubmitting, setErrors }: any
  ) => {
    setApiError("");
    try {
      // Encrypt the key before sending
      const encrypted = await encryptKeys(
        values.secretKey,
        STRIPE_DECRYPT_KEY
      );
      const response = await dispatch<any>(
        setupInvoiceAccount({
          secret_key: encrypted.secretKey,
          account_id: values.accountId,
        }) as any
      ).unwrap();

      if (response?.code === 400) {
        dispatch<any>(
          setToast({
            message: response?.message,
            type: "error",
            title: "Error",
          })
        );
        return;
      }

      //  await dispatch<any>(
      //   getInvoiceList({ offset: 0, search: "", filter_by: -1 })
      // );
      // Check if there are any invoices after setup
      if (
        !response?.data?.has_invoices &&
        Object.keys(response?.data)?.length &&
        response?.code === 200
      ) {
        navigate("/tools/invoices/no-invoice");
      } else if (
        response?.code === 200 &&
        Object.keys(response?.data)?.length === 0
      ) {
        dispatch<any>(
          setToast({
            message: response?.message,
            type: "success",
            title: "Success",
          })
        );
        return;
      } else {
        navigate("/tools/invoices");
      }

      await dispatch<any>(
        getInvoiceList({ offset: 0, search: "", filter_by: -1 })
      );

      // Check for field errors in response.detail
      if (response?.detail && Array.isArray(response.detail)) {
        const fieldErrors: Record<string, string> = {};
        response.detail.forEach((err: any) => {
          if (err.loc?.includes("secret_key")) {
            fieldErrors.secretKey = err.msg;
          }
          if (err.loc?.includes("account_id")) {
            fieldErrors.accountId = err.msg;
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          setSubmitting(false);
          return;
        }
      }

      // If no field errors, show success toast
      dispatch<any>(
        setToast({
          message: response?.message || "Stripe account setup successful!",
          type: "success",
          title: "Success",
        })
      );
      setSubmitting(false);
    } catch (err: any) {
      setApiError(err?.message || "Failed to setup account");
      dispatch<any>(
        setToast({
          message: err?.message || "Failed to setup account",
          type: "error",
          title: "Error",
        })
      );
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "",
        width: "100%",
        paddingTop: "15px",
      }}
    >
      <Button
        sx={{
          mb: 2,
          color: "#6C6F71",
          display: "flex",
          alignItems: "center",
          fontSize: "16px",
          fontWeight: 600,
          gap: 0.5,
          ml: -2,
        }}
        onClick={() => {
          if (Object.keys(data)?.length === 0) {
            setPage();
          } else if (
            data?.invoices?.length === 0 &&
            Object.keys(data)?.length
          ) {
            navigate("/tools/invoices/no-invoice");
          } else {
            navigate("/tools/invoices");
          }
        }}
      >
        <ArrowBackIosNewIcon style={{}} /> Back
      </Button>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: isMobile ? "100%" : isTablet ? "500px" : "600px",
          width: "100%",
          background: "transparent",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 500,
            marginBottom: 3,
            fontSize: isMobile ? "24px" : isTablet ? "28px" : "24px",
          }}
        >
          Setup your Stripe Account
        </Typography>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            submitForm,
            ...formikHelpers
          }) => (
            <Form style={{ width: "100%", marginTop: "2px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Password Field 1 */}
                <FormControl
                  fullWidth
                  error={touched.secretKey && Boolean(errors.secretKey)}
                >
                  <InputLabel
                    htmlFor="secretKey"
                    shrink={true}
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      position: "static",
                      transform: "none",
                      marginBottom: 1,
                      color: "#2C3034 !important",
                    }}
                  >
                    Secret Key
                  </InputLabel>
                  <OutlinedInput
                    id="secretKey"
                    name="secretKey"
                    type={showPassword1 ? "text" : "password"}
                    placeholder="Enter key"
                    value={values.secretKey}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label=""
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword1(!showPassword1)}
                          edge="end"
                        >
                          {showPassword1 ? (
                            <VisibilityOffOutlinedIcon
                              style={{ color: "#97999B" }}
                            />
                          ) : (
                            <VisibilityOutlinedIcon
                              style={{ color: "#97999B" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "2px solid #2977CC",
                      },
                    }}
                  />
                  {touched.secretKey && errors.secretKey && (
                    <FormHelperText
                      style={{
                        marginLeft: 1,
                        fontSize: "16px",
                        color: "red",
                      }}
                    >
                      {Array.isArray(errors.secretKey)
                        ? errors.secretKey.join(", ")
                        : typeof errors.secretKey === "string"
                        ? errors.secretKey
                        : ""}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Password Field 3 */}
                <FormControl
                  fullWidth
                  error={
                    touched.accountId && Boolean(errors.accountId)
                  }
                >
                  <InputLabel
                    htmlFor="accountId"
                    shrink={true}
                    sx={{
                      fontSize: isMobile ? "14px" : "16px",
                      position: "static",
                      transform: "none",
                      marginBottom: 1,
                      color: "#2C3034 !important",
                    }}
                  >
                    Account ID
                  </InputLabel>
                  <OutlinedInput
                    id="accountId"
                    name="accountId"
                    type={showPassword3 ? "text" : "password"}
                    placeholder="Enter account ID"
                    value={values.accountId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label=""
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword3(!showPassword3)}
                          edge="end"
                        >
                          {showPassword3 ? (
                            <VisibilityOffOutlinedIcon
                              style={{ color: "#97999B" }}
                            />
                          ) : (
                            <VisibilityOutlinedIcon
                              style={{ color: "#97999B" }}
                            />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: "8px",
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "2px solid #2977CC",
                      },
                    }}
                  />
                  {touched.accountId && errors.accountId && (
                    <FormHelperText
                      style={{
                        marginLeft: 1,
                        color: "red",
                        fontSize: "16px",
                      }}
                    >
                      {Array.isArray(errors.accountId)
                        ? errors.accountId.join(", ")
                        : typeof errors.accountId === "string"
                        ? errors.accountId
                        : ""}
                    </FormHelperText>
                  )}
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="button"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    marginTop: "10px",
                    borderRadius: "8px",
                    background: "linear-gradient(to right, #2977CC, #1D538F)",
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: 600,
                    textTransform: "none",
                    width: isMobile ? "60%" : "191px",
                    height: "52px",
                    color: "white !important",
                    "&:hover": {
                      background: "linear-gradient(to right, #1D538F, #2977CC)",
                    },
                  }}
                  onClick={() => {
                    if (
                      accountDetails?.account_id &&
                      values.accountId &&
                      accountDetails.account_id !== values.accountId
                    ) {
                      setOpenModal(true);
                    } else {
                      submitForm();
                    }
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: "#fff", mr: 1 }} />
                  ) : (
                    "Connect Account"
                  )}
                </Button>
              </Box>
              <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <ModalWrapper
                  sx={{
                    width: isMobile ? "350px" : "500px",
                    p: 0,
                    height: "300px",
                    backgroundImage: "none",
                    position: "relative",
                    borderRadius: "20px",
                    overflow: "hidden",
                    outline: "none",
                  }}
                >
                  {/* Top right decoration */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      zIndex: 1,
                    }}
                  >
                    <ModalTop />
                  </div>
                  {/* Bottom left decoration */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: -6,
                      left: 0,
                      zIndex: 1,
                    }}
                  >
                    <ModalBottom />
                  </div>
                  {/* Ensure close button is above decorations */}
                  <CloseButtonWrapper
                    sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
                  >
                    <CloseButton onClick={() => setOpenModal(false)}>
                      <CrossIcon />
                    </CloseButton>
                  </CloseButtonWrapper>
                  <Box sx={{ p: 4, pt: 0 }}>
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: isMobile ? "20px" : "24px",
                        color: "#01060B",
                        fontWeight: 700,
                        marginTop: 5,
                        marginBottom: 10,
                      }}
                    >
                      Replace STRIPE Info
                    </div>
                    <div
                      style={{
                        width: isMobile ? "100%" : "350px",
                        textAlign: "center",
                        lineHeight: "24px",
                        margin: "0 auto",
                        fontSize: isMobile ? "12px" : "16px",
                        color: "#6C6F71",
                        fontWeight: 500,
                        fontFamily: "Plus Jakarta Sans",
                      }}
                    >
                      Adding new STRIPE details will erase the existing invoice
                      data. This action cannot be undone. Do you want to
                      proceed?
                    </div>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        mt: 3,
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        sx={{
                          border: "2px solid #2977CC",
                          borderRadius: "8px",
                          width: "192px",
                          height: "51px",
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#2977CC",
                        }}
                        onClick={() => setOpenModal(false)}
                        disabled={isSubmitting}
                      >
                        No
                      </Button>
                      <Button
                        sx={{
                          background:
                            "linear-gradient(to right, #2977CC, #1D538F)",
                          color: "white",
                          width: "192px",
                          height: "51px",
                          fontSize: "16px",
                          fontWeight: 600,
                        }}
                        onClick={async () => {
                          setOpenModal(false);
                          await submitForm();
                        }}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        ) : (
                          "Yes"
                        )}
                      </Button>
                    </Box>
                  </Box>
                </ModalWrapper>
              </Modal>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default InvoiceForm;
